import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Observable, tap } from 'rxjs'
import { AuthGateway } from 'src/app/application/gateway/AuthGateway'
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway'
import { SecurityGateway } from 'src/app/application/gateway/SecurityGateway'
import { TokenResponse } from 'src/app/domain/models/TokenResponse'
import { BasicUser } from 'src/app/domain/models/User'
import { Routes } from 'src/app/infrastructure/routes'

/**
 * Encapsula lo que debe ocurrir justo después de una autenticación exitosa
 * (login o verificación 2FA): persistir el perfil básico, preparar el
 * estado local y navegar a la pantalla correspondiente según el rol y
 * el tipo de perfil del usuario autenticado.
 */
@Injectable({ providedIn: 'root' })
export class AuthRedirectService {

  constructor(
    private readonly _profileService: ProfileGateway,
    private readonly _authService: AuthGateway,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _securityService: SecurityGateway
  ) { }

  /**
   * Obtiene el perfil básico del usuario, persiste el estado local
   * necesario y navega a la página correspondiente.
   * El llamador solo debe manejar `loading` y errores en el `subscribe`.
   */
  handlePostLoginRedirect(): Observable<BasicUser> {
    return this._profileService.getBasicUser().pipe(
      tap((profile) => {
        this.persistProfile(profile)
        this.navigateByRole(profile)
      })
    )
  }

  private persistProfile(profile: BasicUser): void {
    localStorage.setItem('_auth_user', JSON.stringify(profile))
    this._translate.use(profile.lang)
    localStorage.removeItem('_marketplace_port_to_param')
    localStorage.removeItem('_marketplace_country_to_param')
    localStorage.removeItem('_marketplace_country_from_param')
    localStorage.setItem('_dsclm_sh', JSON.stringify(false))
  }

  private navigateByRole(profile: BasicUser): void {
    const role = this._authService.getRole()
    if (role === 'User') {
      this.navigateByProfileType(profile)
    } else if (role === 'Admin') {
      this._router.navigate([Routes.adminManageOrderPage]).then(() => { })
    }
  }

  private navigateByProfileType(profile: BasicUser): void {
    switch (profile.type) {
      case 1:
        this.navigateExporterFlow(profile)
        break
      case 2:
        this.navigateImporterFlow(profile)
        break
      case 4:
        this._router.navigate([Routes.qcUserReviews]).then(() => { })
        break
      case 5:
        this._router.navigate([Routes.forwarderUserReviews]).then(() => { })
        break
      case 6:
        this._router.navigate([Routes.customAgentUserReviews]).then(() => { })
        break
      default:
        this._router.navigate([Routes.homePage]).then(() => { })
    }
  }

  private navigateExporterFlow(profile: BasicUser): void {
    if (!profile.hasProgramLoad && !profile.hasMarketplaceLoad) {
      this._router.navigate([Routes.flowSelectionPage]).then(() => { })
    } else if (profile.hasProgramLoad && !profile.hasMarketplaceLoad) {
      this._router.navigate([Routes.userClosedSales]).then(() => { })
    } else {
      this._router.navigate([Routes.userExportOffers]).then(() => { })
    }
  }

  private navigateImporterFlow(profile: BasicUser): void {
    if (!profile.hasProgramLoad && !profile.hasMarketplaceLoad) {
      this._router.navigate([Routes.flowSelectionPage]).then(() => { })
    } else if (profile.hasProgramLoad && !profile.hasMarketplaceLoad) {
      this._router.navigate([Routes.userClosedSales]).then(() => { })
    } else {
      this._router.navigate([Routes.userImportRequest]).then(() => { })
    }
  }

}
